import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { TitleWithHelp, HelpTooltip } from '../components/ui/help-tooltip';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import {
  DollarSign, TrendingUp, Target, Users, FileText, CheckSquare,
  AlertTriangle, Clock, ArrowRight, BarChart3, Loader2,
  RefreshCw, Send, CheckCircle2, XCircle, Percent, Receipt
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const formatCurrency = (val) => {
  if (!val && val !== 0) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
};
const formatCurrencyShort = (val) => {
  if (!val && val !== 0) return 'R$ 0';
  if (val >= 1000000) return `R$ ${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `R$ ${(val / 1000).toFixed(0)}K`;
  return formatCurrency(val);
};

const DONUT_COLORS = ['#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#DBEAFE', '#EAB308', '#F59E0B', '#8B5CF6'];
const ETAPA_CORES = {
  lead: '#EAB308', em_atendimento: '#3B82F6', proposta_enviada: '#8B5CF6',
  concluido: '#22C55E', descartado: '#EF4444',
};
const TIPO_TAREFA_LABELS = {
  follow_up: 'Follow-up', ligacao: 'Ligação', email: 'E-mail', reuniao: 'Reunião',
  visita: 'Visita', enviar_proposta: 'Enviar Proposta', outro: 'Outro',
};
const ORIGEM_LABELS = {
  whatsapp: 'WhatsApp', site: 'Site', indicacao: 'Indicação', licitacao: 'Licitação',
  evento: 'Evento', telefone: 'Telefone', email: 'E-mail', redes_sociais: 'Redes Sociais', outro: 'Outro',
};
const MOTIVO_LABELS = {
  preco: 'Preço', concorrencia: 'Concorrência', prazo: 'Prazo',
  desistencia: 'Desistência', sem_resposta: 'Sem resposta', fora_perfil: 'Fora do perfil',
  duplicado: 'Duplicado', outro: 'Outro',
};

// ============ KPI CARD ============
const KpiCard = ({ label, value, icon: Icon, bgColor, iconColor, subtext, helpTitle, helpDesc }) => (
  <Card data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`} className="relative overflow-hidden">
    <CardContent className="p-5">
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-xl flex-shrink-0 ${bgColor}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide truncate">{label}</p>
            {helpTitle && <HelpTooltip title={helpTitle} description={helpDesc} iconSize={13} side="top" />}
          </div>
          <p className="text-xl font-bold text-slate-800 mt-1 truncate" title={typeof value === 'string' ? value : undefined}>{value}</p>
          {subtext && <p className="text-[11px] text-slate-400 mt-0.5 truncate" title={subtext}>{subtext}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

// ============ STAT ROW ============
const StatRow = ({ icon: Icon, iconColor, label, value, valueColor }) => (
  <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-50/70">
    <Icon className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />
    <span className="flex-1 text-sm text-slate-600">{label}</span>
    <span className={`text-sm font-bold tabular-nums ${valueColor || 'text-slate-700'}`}>{value}</span>
  </div>
);

// ============ FUNIL BAR ============
const FunilBar = ({ funil }) => {
  if (!funil || funil.length === 0) return null;
  const max = Math.max(...funil.map(f => f.quantidade), 1);
  return (
    <div className="space-y-3">
      {funil.map((etapa) => {
        const pct = max > 0 ? (etapa.quantidade / max) * 100 : 0;
        return (
          <div key={etapa.id} data-testid={`funil-${etapa.id}`} className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-slate-700">{etapa.nome}</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500 text-xs">{formatCurrency(etapa.valor)}</span>
                <Badge variant="outline" className="text-xs font-bold min-w-[28px] justify-center" style={{ borderColor: etapa.cor, color: etapa.cor }}>{etapa.quantidade}</Badge>
              </div>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5">
              <div className="h-2.5 rounded-full transition-all duration-700" style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: etapa.cor }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============ DONUT CHART ============
const DonutChart = ({ data, centerValue, centerLabel, colors }) => {
  const chartData = data.filter(d => d.value > 0);
  if (chartData.length === 0) chartData.push({ name: 'Sem dados', value: 1 });
  return (
    <div className="relative w-48 h-48 mx-auto flex-shrink-0">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={chartData} cx="50%" cy="50%" innerRadius={55} outerRadius={80} paddingAngle={2} dataKey="value" stroke="none">
            {chartData.map((_, i) => (
              <Cell key={i} fill={(colors || DONUT_COLORS)[i % (colors || DONUT_COLORS).length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-3xl font-bold text-slate-800">{centerValue}</span>
        <span className="text-[11px] text-slate-400">{centerLabel}</span>
      </div>
    </div>
  );
};

// ============ BREAKDOWN LIST ============
const BreakdownList = ({ title, items, labelKey, countKey, pctKey, valueKey, colorKey, colors }) => (
  <div>
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</p>
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="text-lg font-bold text-slate-700 w-10 text-right tabular-nums">{item[pctKey]}%</span>
          <div className="w-0.5 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: colorKey ? item[colorKey] : (colors || DONUT_COLORS)[i % (colors || DONUT_COLORS).length] }} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-slate-700 truncate">{item[labelKey]}</p>
            <p className="text-xs text-slate-400">
              {item[countKey]} {item[countKey] === 1 ? 'lead' : 'leads'}
              {valueKey && item[valueKey] ? `, ${formatCurrencyShort(item[valueKey])}` : ''}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ============ MAIN ============
export default function CrmDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('semana');

  const fetchDashboard = useCallback(async (p) => {
    try {
      setLoading(true);
      const res = await api.get(`/crm-oportunidades/dashboard-comercial?periodo=${p || periodo}`);
      setData(res.data);
    } catch { toast.error('Erro ao carregar dashboard'); }
    finally { setLoading(false); }
  }, [periodo]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handlePeriodo = (p) => { setPeriodo(p); fetchDashboard(p); };

  if (loading && !data) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  }
  if (!data) return null;

  const {
    kpis, funil, propostas, tarefas, por_vendedor, por_origem, motivos_perda,
    recentes, propostas_recentes,
    leads_timeline, leads_por_estagio, leads_por_usuario, total_leads,
    contatos: contatosData, tarefas_detalhadas
  } = data;

  // Prepare donut data
  const donutFunil = (leads_por_estagio || []).map(e => ({ name: e.nome, value: e.count }));
  const funilColors = (leads_por_estagio || []).map(e => e.cor);

  const donutLeadsUsuario = (leads_por_usuario || []).map(u => ({ name: u.nome || 'N/A', value: u.count }));

  const donutContatos = [
    { name: 'Pessoa Juridica', value: contatosData?.pessoa_juridica || 0 },
    { name: 'Pessoa Fisica', value: contatosData?.pessoa_fisica || 0 },
  ];

  const donutTarefasTipo = (tarefas_detalhadas?.por_tipo || []).map(t => ({
    name: TIPO_TAREFA_LABELS[t._id] || t._id || 'Outro', value: t.count
  }));

  const donutTarefasUsuario = (tarefas_detalhadas?.por_usuario || []).map(u => ({
    name: u.nome || 'N/A', value: u.count
  }));

  // Timeline data
  const timelineData = (leads_timeline || []).map(t => ({
    data: t._id ? t._id.substring(5) : '', // MM-DD
    leads: t.count,
  }));

  return (
    <div data-testid="crm-dashboard-page" className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <TitleWithHelp
          title="Dashboard Comercial"
          description="Painel consolidado com os principais indicadores de vendas do CRM: oportunidades, propostas, tarefas e desempenho da equipe. Os dados são atualizados em tempo real."
          connections={['Pipeline', 'Propostas', 'Tarefas', 'Clientes']}
        >
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-blue-600" />
              Dashboard Comercial
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">Visão consolidada do CRM e performance de vendas</p>
          </div>
        </TitleWithHelp>
        <Button variant="outline" size="sm" onClick={() => fetchDashboard()} className="gap-2">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="Oport. Ativas" value={kpis.oportunidades_ativas} icon={Target} bgColor="bg-blue-50" iconColor="text-blue-600" helpTitle="Oportunidades Ativas" helpDesc="Total de oportunidades em andamento no pipeline." />
        <KpiCard label="Valor Pipeline" value={formatCurrencyShort(kpis.valor_pipeline)} icon={DollarSign} bgColor="bg-emerald-50" iconColor="text-emerald-600" subtext={`Pond: ${formatCurrencyShort(kpis.valor_ponderado)}`} helpTitle="Valor do Pipeline" helpDesc="Soma dos valores estimados das oportunidades ativas. Ponderado considera a probabilidade." />
        <KpiCard label="Taxa Conversão" value={`${kpis.taxa_conversao}%`} icon={Percent} bgColor="bg-purple-50" iconColor="text-purple-600" subtext={`${kpis.concluidas} ganhas / ${kpis.descartadas} perdidas`} helpTitle="Taxa de Conversão" helpDesc="Percentual de oportunidades ganhas vs total finalizadas." />
        <KpiCard label="Receita Total" value={formatCurrencyShort(kpis.receita_total)} icon={TrendingUp} bgColor="bg-green-50" iconColor="text-green-600" helpTitle="Receita Total" helpDesc="Soma dos valores finais de vendas concluidas." />
        <KpiCard label="Ticket Medio" value={formatCurrencyShort(kpis.ticket_medio)} icon={Receipt} bgColor="bg-amber-50" iconColor="text-amber-600" subtext={`${kpis.concluidas} vendas`} helpTitle="Ticket Medio" helpDesc="Valor medio por venda concluida." />
        <KpiCard label="Total Oport." value={kpis.total_oportunidades} icon={BarChart3} bgColor="bg-slate-100" iconColor="text-slate-600" helpTitle="Total de Oportunidades" helpDesc="Numero total de oportunidades registradas." />
      </div>

      {/* ===== LEADS CHART + FILTERS ===== */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-bold text-slate-800 uppercase tracking-wide">Leads</CardTitle>
              <HelpTooltip title="Gráfico de Leads" description="Evolução de novas oportunidades (leads) ao longo do tempo. Use os filtros para alterar o período de visualização." iconSize={14} />
            </div>
            <div data-testid="periodo-filter" className="flex items-center gap-0 border border-slate-200 rounded-lg overflow-hidden">
              {[
                { key: 'dia', label: 'Dia' },
                { key: 'semana', label: 'Última Semana' },
                { key: 'mes', label: 'Último Mês' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => handlePeriodo(f.key)}
                  className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors ${
                    periodo === f.key
                      ? 'bg-blue-600 text-white'
                      : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {timelineData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={timelineData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="data" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} allowDecimals={false} />
                <ReTooltip
                  contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0', fontSize: 12 }}
                  formatter={(val) => [`${val} leads`, 'Leads']}
                />
                <Line type="monotone" dataKey="leads" stroke="#3B82F6" strokeWidth={2.5} dot={{ r: 4, fill: '#3B82F6' }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-40 text-sm text-slate-400">Nenhum lead no periodo selecionado</div>
          )}
        </CardContent>
      </Card>

      {/* ===== LEADS: ESTÁGIO DO FUNIL + POR USUÁRIO ===== */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-bold text-slate-800 uppercase tracking-wide">Leads</CardTitle>
            <HelpTooltip title="Distribuição de Leads" description="Distribuição das oportunidades por estágio do funil e por vendedor responsável." iconSize={14} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {/* Estágios do Funil */}
            <BreakdownList
              title="Estágios do Funil de Vendas"
              items={(leads_por_estagio || []).map(e => ({ label: e.nome, count: e.count, pct: e.pct, valor: e.valor, cor: e.cor }))}
              labelKey="label" countKey="count" pctKey="pct" valueKey="valor" colorKey="cor"
            />
            {/* Donut Center */}
            <div className="flex justify-center">
              <DonutChart data={donutFunil} centerValue={total_leads || 0} centerLabel="Total de leads" colors={funilColors} />
            </div>
            {/* Leads por Usuário */}
            <BreakdownList
              title="Leads por Usuário"
              items={(leads_por_usuario || []).map(u => ({ label: u.nome || 'N/A', count: u.count, pct: u.pct, valor: u.valor }))}
              labelKey="label" countKey="count" pctKey="pct" valueKey="valor"
            />
          </div>
        </CardContent>
      </Card>

      {/* ===== CONTATOS ===== */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-bold text-slate-800 uppercase tracking-wide">Contatos</CardTitle>
            <HelpTooltip title="Contatos" description="Total de clientes cadastrados no CRM, divididos por tipo (PJ/PF) e por usuário que registrou." iconSize={14} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {/* Donut */}
            <div className="flex flex-col items-center gap-3">
              <DonutChart data={donutContatos} centerValue={contatosData?.total || 0} centerLabel="Todos os contatos" colors={['#3B82F6', '#F59E0B']} />
              <div className="space-y-1 text-sm w-full max-w-[200px]">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">{contatosData?.total > 0 ? Math.round(contatosData.pessoa_juridica / contatosData.total * 100) : 0}%</span>
                  <div className="w-0.5 h-4 bg-blue-500 rounded-full" />
                  <span className="text-slate-600">Empresas: <strong>{contatosData?.pessoa_juridica || 0}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">{contatosData?.total > 0 ? Math.round(contatosData.pessoa_fisica / contatosData.total * 100) : 0}%</span>
                  <div className="w-0.5 h-4 bg-amber-500 rounded-full" />
                  <span className="text-slate-600">Pessoas Fisicas: <strong>{contatosData?.pessoa_fisica || 0}</strong></span>
                </div>
              </div>
            </div>
            {/* spacer */}
            <div />
            {/* Por Usuário */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Quantidade por Usuário</p>
              <div className="space-y-3">
                {(contatosData?.por_usuario || []).length > 0 ? (
                  contatosData.por_usuario.map((u, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xl font-bold text-slate-700 w-12 text-right tabular-nums">{u.pct}%</span>
                      <div className="w-0.5 h-9 rounded-full bg-blue-400 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{u.nome || 'Sem atribuição'}</p>
                        <p className="text-xs text-slate-400">{u.count} contatos</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400">Sem dados</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ===== TAREFAS DETALHADAS ===== */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-bold text-slate-800 uppercase tracking-wide">Tarefas</CardTitle>
            <HelpTooltip title="Tarefas" description="Visão geral de todas as tarefas registradas no CRM, distribuídas por tipo e por usuário responsável." iconSize={14} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            {/* Donut */}
            <div className="flex justify-center">
              <DonutChart
                data={donutTarefasTipo.length > 0 ? donutTarefasTipo : [{ name: 'Sem tarefas', value: 1 }]}
                centerValue={tarefas_detalhadas?.total || 0}
                centerLabel="Todas as tarefas"
                colors={['#93C5FD', '#BFDBFE', '#60A5FA', '#3B82F6', '#F59E0B']}
              />
            </div>
            {/* Por Tipo */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Tarefas por Tipo</p>
              <div className="space-y-3">
                {(tarefas_detalhadas?.por_tipo || []).length > 0 ? (
                  tarefas_detalhadas.por_tipo.map((t, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xl font-bold text-slate-700 w-12 text-right tabular-nums">{t.pct}%</span>
                      <div className="w-0.5 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: ['#93C5FD', '#BFDBFE', '#60A5FA', '#3B82F6', '#F59E0B'][i % 5] }} />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{TIPO_TAREFA_LABELS[t._id] || t._id || 'Outro'}</p>
                        <p className="text-xs text-slate-400">{t.count} {t.count === 1 ? 'Tarefa' : 'Tarefas'}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400">Sem tarefas</p>
                )}
              </div>
            </div>
            {/* Por Usuário */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Tarefas por Usuário</p>
              <div className="space-y-3">
                {(tarefas_detalhadas?.por_usuario || []).length > 0 ? (
                  tarefas_detalhadas.por_usuario.map((u, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xl font-bold text-slate-700 w-12 text-right tabular-nums">{u.pct}%</span>
                      <div className="w-0.5 h-9 rounded-full flex-shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                      <div>
                        <p className="text-sm font-medium text-slate-700">{u.nome || 'Sem atribuição'}</p>
                        <p className="text-xs text-slate-400">{u.count} {u.count === 1 ? 'tarefa' : 'tarefas'}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-slate-400">Sem dados</p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Funil + Propostas + Tarefas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold text-slate-700">Funil de Vendas</CardTitle>
                <HelpTooltip title="Funil de Vendas" description="Distribuição das oportunidades por etapa do pipeline com valores." iconSize={14} />
              </div>
              <Button variant="ghost" size="sm" className="gap-1 text-xs text-blue-600" onClick={() => navigate('/crm/pipeline')}>Ver Pipeline <ArrowRight className="w-3 h-3" /></Button>
            </div>
          </CardHeader>
          <CardContent><FunilBar funil={funil} /></CardContent>
        </Card>
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base font-semibold text-slate-700">Propostas</CardTitle>
                  <HelpTooltip title="Propostas Comerciais" description="Resumo do status das propostas comerciais." iconSize={14} />
                </div>
                <Button variant="ghost" size="sm" className="gap-1 text-xs text-blue-600" onClick={() => navigate('/crm/propostas')}>Ver <ArrowRight className="w-3 h-3" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <StatRow icon={FileText} iconColor="text-gray-400" label="Rascunhos" value={propostas.rascunho} />
              <StatRow icon={Send} iconColor="text-blue-500" label="Enviadas" value={propostas.enviadas} valueColor="text-blue-600" />
              <StatRow icon={CheckCircle2} iconColor="text-green-500" label="Aceitas" value={propostas.aceitas} valueColor="text-green-600" />
              <StatRow icon={XCircle} iconColor="text-red-400" label="Recusadas" value={propostas.recusadas} valueColor="text-red-600" />
              <div className="border-t border-slate-100 pt-2 mt-2 space-y-1.5">
                <div className="flex items-center justify-between px-3 text-sm"><span className="text-slate-500">Valor aceitas</span><span className="font-bold text-green-600">{formatCurrency(propostas.valor_aceitas)}</span></div>
                {propostas.taxa_aceite > 0 && <div className="flex items-center justify-between px-3 text-sm"><span className="text-slate-500">Taxa de aceite</span><span className="font-bold text-purple-600">{propostas.taxa_aceite}%</span></div>}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base font-semibold text-slate-700">Tarefas</CardTitle>
                  <HelpTooltip title="Tarefas Pendentes" description="Resumo das tarefas de follow-up pendentes." iconSize={14} />
                </div>
                <Button variant="ghost" size="sm" className="gap-1 text-xs text-blue-600" onClick={() => navigate('/crm/tarefas')}>Ver <ArrowRight className="w-3 h-3" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <StatRow icon={CheckSquare} iconColor="text-blue-500" label="Pendentes" value={tarefas.pendentes} valueColor="text-blue-600" />
              <StatRow icon={AlertTriangle} iconColor="text-red-500" label="Atrasadas" value={tarefas.atrasadas} valueColor={tarefas.atrasadas > 0 ? 'text-red-600' : 'text-slate-400'} />
              <StatRow icon={Clock} iconColor="text-amber-500" label="Para hoje" value={tarefas.hoje} valueColor="text-amber-600" />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Ranking + Origens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2"><Users className="w-4 h-4" /> Desempenho por Vendedor</CardTitle>
              <HelpTooltip title="Ranking de Vendedores" description="Ranking por valor de vendas concluidas." iconSize={14} />
            </div>
          </CardHeader>
          <CardContent>
            {por_vendedor && por_vendedor.length > 0 ? (
              <div className="space-y-3">
                {por_vendedor.map((v, i) => {
                  const taxa = v.total > 0 ? Math.round((v.concluidas / v.total) * 100) : 0;
                  return (
                    <div key={v._id || i} data-testid={`vendedor-${i}`} className="flex items-center gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-50 text-slate-500'}`}>{i + 1}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{v.nome || 'Sem nome'}</p>
                        <p className="text-xs text-slate-400">{v.total} oport. | {v.concluidas} ganhas | {taxa}%</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold text-green-600">{formatCurrencyShort(v.valor_ganho)}</p>
                        <p className="text-xs text-slate-400">{formatCurrencyShort(v.valor_total)} pipeline</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400 text-center py-6">Nenhum vendedor atribuido</p>}
          </CardContent>
        </Card>
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold text-slate-700">Origem dos Leads</CardTitle>
                <HelpTooltip title="Origem dos Leads" description="Canais de aquisição de oportunidades." iconSize={14} />
              </div>
            </CardHeader>
            <CardContent>
              {por_origem && por_origem.length > 0 ? (
                <div className="space-y-2">
                  {por_origem.map((o, i) => {
                    const tot = por_origem.reduce((s, x) => s + x.count, 0);
                    return (
                      <div key={o._id || i} className="flex items-center gap-2 text-sm">
                        <span className="flex-1 text-slate-600">{ORIGEM_LABELS[o._id] || o._id || 'Outro'}</span>
                        <span className="text-xs text-slate-400">{tot > 0 ? Math.round(o.count / tot * 100) : 0}%</span>
                        <Badge variant="outline" className="text-xs min-w-[28px] justify-center">{o.count}</Badge>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>}
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base font-semibold text-slate-700">Motivos de Perda</CardTitle>
                <HelpTooltip title="Motivos de Perda" description="Razões pelas quais oportunidades foram perdidas." iconSize={14} />
              </div>
            </CardHeader>
            <CardContent>
              {motivos_perda && motivos_perda.length > 0 ? (
                <div className="space-y-2">
                  {motivos_perda.map((m, i) => (
                    <div key={m._id || i} className="flex items-center gap-2 text-sm">
                      <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                      <span className="flex-1 text-slate-600">{MOTIVO_LABELS[m._id] || m._id || 'Outro'}</span>
                      <Badge variant="outline" className="text-xs text-red-600 border-red-200">{m.count}</Badge>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-slate-400 text-center py-4">Nenhuma perda registrada</p>}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recentes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold text-slate-700">Oportunidades Recentes</CardTitle>
              <HelpTooltip title="Oportunidades Recentes" description="Últimas 10 oportunidades atualizadas." iconSize={14} />
            </div>
          </CardHeader>
          <CardContent>
            {recentes && recentes.length > 0 ? (
              <div className="space-y-1">
                {recentes.map((r, i) => (
                  <div key={r.id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate('/crm/pipeline')}>
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: ETAPA_CORES[r.etapa] || '#6B7280' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{r.titulo}</p>
                      <p className="text-xs text-slate-400 truncate">{r.cliente_nome || r.contato_nome || '-'} {r.responsavel_nome ? `| ${r.responsavel_nome}` : ''}</p>
                    </div>
                    <span className="text-sm font-semibold text-slate-600 whitespace-nowrap flex-shrink-0">{formatCurrencyShort(r.valor_estimado)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 text-center py-6">Nenhuma oportunidade</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold text-slate-700">Propostas Recentes</CardTitle>
              <HelpTooltip title="Propostas Recentes" description="Últimas 5 propostas comerciais criadas." iconSize={14} />
            </div>
          </CardHeader>
          <CardContent>
            {propostas_recentes && propostas_recentes.length > 0 ? (
              <div className="space-y-1">
                {propostas_recentes.map((p, i) => {
                  const cfg = { rascunho: { c: 'text-gray-500', i: FileText }, enviada: { c: 'text-blue-600', i: Send }, aceita: { c: 'text-green-600', i: CheckCircle2 }, recusada: { c: 'text-red-600', i: XCircle } }[p.status] || { c: 'text-gray-500', i: FileText };
                  return (
                    <div key={p.id || i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => navigate('/crm/propostas')}>
                      <cfg.i className={`w-4 h-4 flex-shrink-0 ${cfg.c}`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{p.numero} - {p.cliente_nome || '-'}</p>
                        <p className="text-xs text-slate-400">{p.vendedor_nome || '-'} | {p.created_at ? new Date(p.created_at).toLocaleDateString('pt-BR') : '-'}</p>
                      </div>
                      <span className="text-sm font-semibold text-slate-600 whitespace-nowrap flex-shrink-0">{formatCurrencyShort(p.total)}</span>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-sm text-slate-400 text-center py-6">Nenhuma proposta</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
